import { supabase } from "@/integrations/supabase/client";

/**
 * Web Push (VAPID) client helpers — single source of truth for the public
 * key and the subscribe/rotate logic shared by usePushNotifications and
 * useWebPushSubscription.
 *
 * IMPORTANT: this public key must match the VAPID_PRIVATE_KEY secret
 * configured for the send-push edge function, and the copy of
 * VAPID_PUBLIC_KEY inside supabase/functions/send-push/index.ts.
 */
export const VAPID_PUBLIC_KEY =
  "BJTtTK64NnQBrZH9AhqQpUV61P3kM40n_VrTJtwtNjgh4H9ijG_8eZVsT5kaONKpP8pffEpLQFukHHEYVlZK9gI";

export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Returns a push subscription bound to the CURRENT VAPID public key.
 *
 * Handles key rotation: a subscription minted under an old server key
 * cannot be delivered to with the new private key (the push service
 * rejects it with 403 VAPID mismatch). If the existing subscription's
 * applicationServerKey differs from VAPID_PUBLIC_KEY — or can't be
 * verified — it is unsubscribed, its DB row removed, and a fresh
 * subscription is created under the current key.
 */
export async function getOrCreatePushSubscription(
  registration: ServiceWorkerRegistration,
): Promise<PushSubscription> {
  let subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    const currentKey = subscription.options?.applicationServerKey ?? null;
    const matchesCurrentKey =
      currentKey !== null && arrayBufferToBase64Url(currentKey) === VAPID_PUBLIC_KEY;
    if (!matchesCurrentKey) {
      const staleEndpoint = subscription.endpoint;
      try { await subscription.unsubscribe(); } catch { /* noop */ }
      try {
        await supabase.from("push_subscriptions").delete().eq("endpoint", staleEndpoint);
      } catch { /* noop */ }
      subscription = null;
    }
  }

  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
    });
  }

  return subscription;
}

/** Persist a subscription's keys for the logged-in user. */
export async function savePushSubscription(
  userId: string,
  subscription: PushSubscription,
): Promise<boolean> {
  const p256dhKey = subscription.getKey("p256dh");
  const authKey = subscription.getKey("auth");
  if (!p256dhKey || !authKey) return false;

  await supabase.from("push_subscriptions").upsert(
    {
      user_id: userId,
      endpoint: subscription.endpoint,
      p256dh: arrayBufferToBase64Url(p256dhKey),
      auth: arrayBufferToBase64Url(authKey),
    },
    { onConflict: "user_id,endpoint" }
  );
  return true;
}
